import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useInView } from 'react-intersection-observer';
import { useRouter } from 'next/router';
import { Loader } from '../../Loader';

const PAGE_SIZE = 12;

const VirtualizedGrid = ({
  ItemRenderer,
  fetchItems,
  totalNumberOfItems,
  initialPages,
  initialPageParams,
  columnCount,
  height,
  width,
  getGridHeader,
  rowsPerPage,
  offset,
  toggleHeader,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const parentRef = useRef(null);
  const { ref: bottomRef, inView: bottomInView } = useInView({
    root: parentRef.current,
    threshold: 1,
  });

  const [containerWidth, setContainerWidth] = useState(width); // initial fallback
  const aspectRatio =
    width < 500 ? 130 / 290 : width < 830 ? 376 / 395 : width < 1180 ? 286 / 290 : 400 / 395;
  const rowHeight = Math.max(
    (containerWidth / columnCount) * aspectRatio,
    width < 500 ? 290 : width < 830 ? 395 : width < 1180 ? 290 : 395,
  );

  const [initialPage, setInitialPage] = useState(
    Number.parseInt((router.query.pageNumber as string) || '1', 10),
  );
  const [hasScrolledToInitial, setHasScrolledToInitial] = useState(false);
  const [blockingPage, setBlockingPage] = useState(null);
  const [queryEnabled, setQueryEnabled] = useState(false);
  const hasLoadedPlaceholder = useRef<Set<number | undefined>>(new Set());

  const [hasScrollSettled, setHasScrollSettled] = useState(false); // 👈 scroll "done"
  const scrollTimeout = useRef(null);

  const fetchData = useCallback(
    async (props) => {
      const start = (props.pageParam - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const res = await fetchItems({ page: props.pageParam, pageSize: 12 });

      const items = res?.data || [];

      return {
        items,
        prevPage: props.pageParam > 1 ? props.pageParam - 1 : undefined,
        nextPage: end < totalNumberOfItems ? props.pageParam + 1 : undefined,
        hasMore: end < totalNumberOfItems,
        page: props.pageParam,
      };
    },
    [totalNumberOfItems],
  );

  useEffect(() => {
    if (hasScrollSettled && hasScrolledToInitial) {
      // ✅ Scrolling has finished

      setQueryEnabled(true); // for example
    }
  }, [hasScrollSettled, hasScrolledToInitial]);

  useEffect(() => {
    if (router.isReady) {
      const page = Number.parseInt((router.query.pageNumber as string) || '1', 10);
      setInitialPage(page);
    }
  }, [router.isReady, router.query.pageNumber]);

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    queryKey: ['items'],
    queryFn: fetchData,
    enabled: false,
    initialPageParam: initialPage,
    getPreviousPageParam: (firstPage) => firstPage.prevPage,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    initialData: () => {
      return {
        pages: initialPages,
        pageParams: initialPageParams,
      };
    },
  });

  const items = data?.pages.flatMap((page) => page.items || []) || [];

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(items.length / columnCount),
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 4,
  });

  useEffect(() => {
    if (!parentRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      rowVirtualizer.measure(); // 👈 re-measure all rows
    });
    observer.observe(parentRef.current);
    return () => observer.disconnect();
  }, [columnCount, rowVirtualizer]);

  // useEffect(() => {
  //   const handleResize = () => {
  //     rowVirtualizer.measure(); // 🔁 Force full recalculation of all item sizes
  //   };

  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, [rowVirtualizer]);
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl) return;

    if (initialPage === 1) {
      setHasScrollSettled(true);
      return;
    }

    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => {
        setHasScrollSettled(true);
      }, 100); // ~100ms after last scroll event
    };

    scrollEl.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [parentRef.current, initialPage]);

  // Load more pages when intersection triggers

  useEffect(() => {
    if (bottomInView && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [bottomInView, hasNextPage, isFetching]);

  // Scroll to initial page only once when data is ready

  useEffect(() => {
    if (initialPage === 1) {
      setHasScrolledToInitial(true);
    } else if (!hasScrolledToInitial && initialPage !== null && items.length > 0) {
      const scrollToIndex = Math.floor(((initialPage - 1) * PAGE_SIZE) / columnCount);
      rowVirtualizer.scrollToIndex(scrollToIndex, { align: 'start' });

      setHasScrolledToInitial(true);
    }
  }, [items.length, columnCount, hasScrolledToInitial, initialPage]);

  // here is to setup the current page

  // here is to scroll up and replace fake data

  useEffect(() => {
    if (!queryEnabled) return;
    const observer = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          const el = entry.target as any;
          if (!entry.isIntersecting) continue;

          if (!el.dataset.placeholder || !el.dataset.page || blockingPage) continue;

          const page = Number.parseInt(el.dataset.page, 10);

          if (Number.isNaN(page) || hasLoadedPlaceholder.current.has(page)) continue;

          setBlockingPage(page);

          try {
            // eslint-disable-next-line no-await-in-loop

            // eslint-disable-next-line no-await-in-loop
            const newPage = await fetchItems({ page, pageSize: 12 });

            hasLoadedPlaceholder.current = new Set([...hasLoadedPlaceholder.current, page]);

            queryClient.setQueryData(['items'], (oldData: any) => {
              if (!oldData) return oldData;

              const updatedPages = oldData.pages.map((p) =>
                p.page === page
                  ? {
                      ...p,
                      items: newPage.data,
                    }
                  : p,
              );
              return { ...oldData, pages: updatedPages };
            });
          } catch {
            // console.log(failed)
          }

          setBlockingPage(null);
        }
      },
      {
        root: parentRef.current,
        threshold: 0.5,
      },
    );

    const placeholders = parentRef.current?.querySelectorAll('[data-placeholder="true"]');
    placeholders?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [items, blockingPage, queryEnabled]);

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (!initialPages) return;

    // Reset page to 1 in the URL
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, pageNumber: 1 },
      },
      undefined,
      { shallow: true },
    );

    // Clear internal states
    setInitialPage(1);
    // setHasScrolledToInitial(true);
    // setHasScrollSettled(false);
    // setQueryEnabled(false);
    hasLoadedPlaceholder.current = new Set();

    // Clear React Query cache
    queryClient.setQueryData(['items'], {
      pages: initialPages,
      pageParams: initialPageParams,
    });

    // Reset scroll position
    rowVirtualizer.scrollToOffset(0);
  }, [initialPages]);

  if (initialPage === null) return null;

  return (
    <div
      ref={parentRef}
      className="overflow-auto h-screen w-full relative no-scrollbar"
      style={{
        visibility: queryEnabled ? 'visible' : 'hidden',
        width: width - offset.left,
      }}
      onScroll={() => {
        // scrollTop comes from here now
        if (queryEnabled) {
          const scrollTop = parentRef.current?.scrollTop ?? 0;
          const virtualRows = rowVirtualizer.getVirtualItems();
          const firstVisibleRow = virtualRows.find((v) => v.start >= scrollTop);
          if (!firstVisibleRow) return;

          const firstIndex = firstVisibleRow.index;
          const firstItemIndex = firstIndex * columnCount;
          const currentPage = Math.floor(firstItemIndex / PAGE_SIZE) + 1;

          if (Number.parseInt((router.query.pageNumber as string) || '1', 10) < currentPage) {
            toggleHeader(false);
          } else if (
            Number.parseInt((router.query.pageNumber as string) || '1', 10) > currentPage
          ) {
            toggleHeader(true);
          }

          const urlPage = Number.parseInt((router.query.pageNumber as string) || '1', 10) || 1;
          if (urlPage !== currentPage) {
            router.replace(
              {
                pathname: router.pathname,
                query: { ...router.query, pageNumber: currentPage },
              },
              undefined,
              { shallow: true },
            );
          }
        }
      }}
    >
      <div>{getGridHeader?.()}</div>
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
          width: width - offset.left * 1.5,
        }}
        className="no-scrollbar"
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow, columnIndex) => {
          const startIdx = virtualRow.index * columnCount;
          const rowItems = items.slice(startIdx, startIdx + columnCount);
          const isFirstRow = virtualRow.index === 0;

          return (
            <div
              key={virtualRow.key}
              className="grid absolute left-0 right-0"
              ref={(el) => {
                if (el) rowVirtualizer.measureElement(el);
              }}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: `${rowHeight}px`,
                gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                gap: '0px',
              }}
            >
              {rowItems.map((item, i) => {
                const index = startIdx + i;
                const page = Math.floor(index / PAGE_SIZE) + 1;
                const isPlaceholder = !item?.id;
                const isFirstColumn = i === 0;
                const itemIndex = virtualRow.index * columnCount + columnIndex;
                const itemPosition = (page - 1) * PAGE_SIZE + itemIndex;
                return (
                  <div
                    key={item.id || `${virtualRow.index}-${i}`}
                    data-placeholder={isPlaceholder ? 'true' : undefined}
                    data-page={page}
                    style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      borderRight: '1px solid #000',
                      borderLeft: isFirstColumn ? '1px solid #000' : 'none',
                      borderBottom: '1px solid #000',
                      borderTop: isFirstRow ? '1px solid #000' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      {item?.id ? <ItemRenderer data={item} position={itemPosition} /> : '  '}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div
        ref={bottomRef}
        style={{
          margin: height / rowsPerPage / 4,

          width: width - offset.left * 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {isFetching && hasNextPage && (
          <Loader
            style={{
              maxWidth: 400,
              minWidth: 250,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default VirtualizedGrid;
